import React, { useCallback, useMemo } from 'react';
import type { WorkOrderTemplate } from '#/graphql/types';
import type { FormAction, FormItem } from '#/common/components/Form';
import Form from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import { InitializedEventInfo } from 'devextreme/events';
import dxForm from 'devextreme/ui/form';
import { SubmitWorkOrderTemplate } from '../../utils/types';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import {
  getItemOptions,
  setEditorOptions,
  setItemOptions,
} from '#/common/components/Form/formItemOption';

export interface WOTemplateFormProps {
  woTemplate: WorkOrderTemplate;
  fields: FormItem<WorkOrderTemplate>[];
  submit?: SubmitWorkOrderTemplate;
  deleteWoTemplate?: () => unknown;
  title: string;
}

const WOTemplateForm: React.FC<WOTemplateFormProps> = ({
  woTemplate,
  fields,
  title,
  submit,
  deleteWoTemplate,
}) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();

  const actions = useMemo(() => {
    const result: FormAction<WorkOrderTemplate>[] = [];

    if (deleteWoTemplate) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const { category, executionInterval } = e.record as WorkOrderTemplate;

          let msg = 'Deleting this template is permanent. Are you sure you want to proceed?';

          if (category === 'preventive' && executionInterval?.length) {
            msg =
              'This Work Order Template is configured for preventive recurrence. Deleting it will stop all future Work Orders from being created. Existing Work Orders will not be affected. This action is permanent.';
          }

          const sure = await confirm('error', msg);

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteWoTemplate();
          e.setIsLoading(false);

          navigate('/work-orders/templates');
        },
      });
    }

    if (woTemplate.id) {
      result.push({
        label: 'Create Copy',
        type: 'normal',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          navigate(`/work-orders/templates/new?copyFrom=${woTemplate.id}`);
        },
      });
    }

    if (submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          const hasId = !!e.record?.id?.length;
          const sure =
            !hasId ||
            (await confirm(
              'info',
              `Work Orders already created out of this template will not be affected by the change.`,
            ));

          if (!sure && hasId) {
            return;
          }

          try {
            e.setIsLoading(true);
            const { data, errors } = (await submit(e.record)) as {
              data?: WorkOrderTemplate;
              errors?: string[];
            };
            e.setIsLoading(false);

            if (errors) {
              void alert('error', errors.join(`;\n`));
              return;
            }

            if (!e.record?.id && data) {
              navigate(`/work-orders/templates/${data.id}`, { replace: true });
            }

            e.clearDirty();
            void alert('success', 'Changes saved!');
          } catch (err) {
            e.setIsLoading(false);
            console.error('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, deleteWoTemplate, navigate, submit, woTemplate]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;

    if (!form) return;

    const formData = form.option('formData');

    const { tenantId } = formData ?? {};

    let accessTo: { resource: string; ids: string[] } | undefined = undefined;
    if (tenantId) {
      accessTo = { resource: 'tenants', ids: [tenantId] };
    }

    setEditorOptions(form, 'assignedToId', {
      dataStoreOptions: {
        ...getItemOptions(form, 'assignedToId')?.editorOptions?.dataStoreOptions,
        accessTo,
      },
    });

    // Execution interval must be Required when category is Preventive
    const shouldBeRequired = formData?.category === 'preventive';
    setItemOptions(form, 'executionInterval', 'isRequired', shouldBeRequired);

    // If there is asset type or manufacturer change, enable/disable asset manufacturer
    const makeOptions = { disabled: !formData.assetTypeId };
    const modelOptions = { disabled: !formData.assetManufacturerId };
    setEditorOptions(form, 'assetManufacturerId', makeOptions);
    setEditorOptions(form, 'assetModelId', modelOptions);
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField: field, value } = e;
    const formData = form.option('formData');

    // Execution interval must be Required when category is Preventive
    if (field === 'category') {
      setItemOptions(form, 'executionInterval', 'isRequired', value === 'preventive');
    }
    // If there is asset type change, enable/disable asset manufacturer
    if (field === 'assetTypeId') {
      const makeOptions = { disabled: !formData.assetTypeId };
      setEditorOptions(form, 'assetManufacturerId', makeOptions);
      form.updateData('assetModelId', null);
      form.updateData('assetManufacturerId', null);
    }

    // If there is manufacturer change, enable/disable asset model
    if (field === 'assetManufacturerId' || field === 'assetTypeId') {
      const modelOptions = { disabled: !formData.assetManufacturerId };
      setEditorOptions(form, 'assetModelId', modelOptions);
      form.updateData('assetModelId', null);
    }
  }, []);

  return (
    <Form
      fields={fields}
      record={woTemplate}
      actions={actions}
      onContentReady={onContentReady}
      onFieldDataChanged={onFieldDataChanged}
      supportTenant
    />
  );
};

export default React.memo(WOTemplateForm);
